/**
 * §21.1 Finanzas — P&L básico del periodo (DATO DEL DUEÑO; solo financials:view,
 * gateado en Dashboard + API 403). Ingresos (NETOS de IVA, consistentes con el
 * margen V40) − gastos por categoría = utilidad ($ y %), + flujo de efectivo básico.
 * Reusa DatePeriodFilter, api() y Recharts.
 */
import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { TrendingUp, TrendingDown, Wallet } from 'lucide-react';
import { api } from '../lib/api';
import { DatePeriodFilter, periodToQuery, periodLabel, defaultPeriod, type PeriodValue } from '../components/DatePeriodFilter';

const mxn = (v: any) =>
  `$${Number(v ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v: any) => `${Number(v ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

/** V220 — renglones del desglose de ingreso. Vacío si solo hay ventas de órdenes (un tenant
 *  sin membresías ni reservas en línea ve el estado de resultados de siempre). */
export function incomeBreakdownRows(b?: { orders: number; memberships: number; reservations: number }): Array<{ label: string; amount: number }> {
  if (!b || (b.memberships === 0 && b.reservations === 0)) return [];
  const rows = [{ label: 'Ventas (órdenes)', amount: b.orders }];
  if (b.memberships !== 0) rows.push({ label: 'Membresías', amount: b.memberships });
  if (b.reservations !== 0) rows.push({ label: 'Reservas en línea (neto de reembolsos)', amount: b.reservations });
  return rows;
}

interface Pnl {
  from: string; to: string;
  income: number; expensesTotal: number; profit: number; profitPct: number;
  /** V220 — de dónde viene el ingreso (suman `income`). */
  incomeBreakdown?: { orders: number; memberships: number; reservations: number };
  expensesByCategory: Array<{ category: string; amount: number }>;
  cashFlow: { inflow: number; outflow: number; net: number };
}

function Tile({ label, value, hint, accent }: { label: string; value: string; hint?: string; accent?: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-5 py-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`font-serif text-2xl tabular-nums ${accent ?? 'text-slate-900'}`}>{value}</p>
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

export function PnlView() {
  const [period, setPeriod] = useState<PeriodValue>(defaultPeriod());
  const [data, setData] = useState<Pnl | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api(`/finance/pnl?${new URLSearchParams(periodToQuery(period)).toString()}`)
      .then((r) => { if (!cancelled) setData(r as Pnl); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  const profitTone = (data?.profit ?? 0) >= 0 ? 'text-emerald-700' : 'text-rose-600';
  const netTone = (data?.cashFlow?.net ?? 0) >= 0 ? 'text-emerald-700' : 'text-rose-600';

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-slate-900">Ingresos vs gastos</h2>
          <p className="text-sm text-slate-500">Ingresos (sin IVA) − gastos = utilidad — {periodLabel(period)}.</p>
        </div>
        <DatePeriodFilter value={period} onChange={setPeriod} />
      </div>

      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : !data ? (
        <p className="text-sm text-slate-400">Sin datos para el período.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile label="Ingresos (sin IVA)" value={mxn(data.income)} />
            <Tile label="Gastos" value={mxn(data.expensesTotal)} />
            <Tile label="Utilidad" value={mxn(data.profit)} accent={profitTone} />
            <Tile label="Margen de utilidad" value={pct(data.profitPct)} accent={profitTone} />
          </div>

          {/* Resumen P&L */}
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="mb-3 flex items-center gap-2 font-serif text-lg text-slate-900">
              <TrendingUp className="h-4 w-4 text-emerald-600" /> Estado de resultados
            </h3>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-slate-100">
                <tr><td className="py-2 text-slate-600">Ingresos (netos de IVA)</td><td className="py-2 text-right tabular-nums text-slate-800">{mxn(data.income)}</td></tr>
                {/* V220 — desglose: solo las fuentes con movimiento. */}
                {incomeBreakdownRows(data.incomeBreakdown).map((r) => (
                  <tr key={r.label}><td className="py-1.5 pl-4 text-xs text-slate-500">{r.label}</td><td className="py-1.5 text-right text-xs tabular-nums text-slate-600">{mxn(r.amount)}</td></tr>
                ))}
                <tr><td className="py-2 text-slate-600">(−) Gastos</td><td className="py-2 text-right tabular-nums text-rose-600">−{mxn(data.expensesTotal)}</td></tr>
                <tr className="border-t-2 border-slate-200"><td className="py-2 font-semibold text-slate-900">Utilidad</td><td className={`py-2 text-right font-semibold tabular-nums ${profitTone}`}>{mxn(data.profit)}</td></tr>
              </tbody>
            </table>
          </section>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Gastos por categoría */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="mb-3 flex items-center gap-2 font-serif text-lg text-slate-900">
                <TrendingDown className="h-4 w-4 text-rose-500" /> Gastos por categoría
              </h3>
              {data.expensesByCategory.length === 0 ? (
                <p className="text-sm text-slate-400">Sin gastos en el período.</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={Math.max(140, data.expensesByCategory.length * 34)}>
                    <BarChart layout="vertical" data={data.expensesByCategory} margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
                      <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false}
                        tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`)} />
                      <YAxis type="category" dataKey="category" width={110} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip formatter={(v: any) => mxn(v)} />
                      <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
                        {data.expensesByCategory.map((_, i) => <Cell key={i} fill="#b45309" />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <table className="mt-2 w-full text-sm">
                    <tbody className="divide-y divide-slate-100">
                      {data.expensesByCategory.map((c) => (
                        <tr key={c.category}>
                          <td className="py-1.5 text-slate-600">{c.category}</td>
                          <td className="py-1.5 text-right tabular-nums text-slate-700">{mxn(c.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </section>

            {/* Flujo de efectivo básico */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="mb-3 flex items-center gap-2 font-serif text-lg text-slate-900">
                <Wallet className="h-4 w-4 text-slate-500" /> Flujo de efectivo
              </h3>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  <tr><td className="py-2 text-slate-600">Entradas (cobros del período)</td><td className="py-2 text-right tabular-nums text-emerald-700">{mxn(data.cashFlow.inflow)}</td></tr>
                  <tr><td className="py-2 text-slate-600">Salidas (gastos del período)</td><td className="py-2 text-right tabular-nums text-rose-600">−{mxn(data.cashFlow.outflow)}</td></tr>
                  <tr className="border-t-2 border-slate-200"><td className="py-2 font-semibold text-slate-900">Flujo neto</td><td className={`py-2 text-right font-semibold tabular-nums ${netTone}`}>{mxn(data.cashFlow.net)}</td></tr>
                </tbody>
              </table>
              <p className="mt-3 text-[11px] text-slate-400">Entradas = cobros registrados (bruto, lo que entró a caja). Las salidas son los gastos del período.</p>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
